import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react'
import Text, { TextScale } from 'particles/Text'
import Label, { LabelSize, LinkConfig } from 'particles/Label'
import { useTranslation } from 'react-i18next'
import styled, { CSSObject } from 'particles/themes/styled'
import Icon, { IconName, IconSize } from 'particles/Icon'
import Tooltip from 'particles/Tooltip'
import IconButton from 'particles/Button/IconButton'
import { MenuItemSize } from 'particles/MenuItem'
import Menu from 'particles/Menu'
import Chip, { ChipProps } from 'particles/Chip'
import { MenuItemProps as MenuItemTextProps } from 'particles/MenuItem/Text'
import { MenuItemProps as MenuItemCheckboxProps } from 'particles/MenuItem/Checkbox'
import { getAlignItems } from 'particles/utils'
import CommentContainer from 'components/Shared/Comment'
import Paper from '@mui/material/Paper'
import { Loader } from 'particles/Button'

export interface OptionsState {
    label: string
    value: any
    category?: string
    secondInfo?: string
}

export type DropdownSize = 'm' | 's' | 'xs'

const sizeMap: Record<
    DropdownSize,
    { menuSize: MenuItemSize; iconSize: IconSize; style: CSSObject; readOnlyStyle: CSSObject; paperHeight: string }
> = {
    m: {
        iconSize: '20px',
        menuSize: 'm',
        style: {
            padding: '0px 11px',
            maxHeight: '40px',
        },
        readOnlyStyle: { padding: '10px 11px' },
        paperHeight: '296px',
    },
    s: {
        iconSize: '16px',
        menuSize: 's',
        style: { padding: '0px 7px', maxHeight: '32px' },
        readOnlyStyle: { padding: '6px 7px' },
        paperHeight: '232px',
    },
    xs: {
        iconSize: '16px',
        menuSize: 's',
        style: { padding: '0px 7px', maxHeight: '28px' },
        readOnlyStyle: { padding: '4px 7px' },
        paperHeight: '232px',
    },
}

const StyledChip = styled(Chip)`
    min-width: 0;
`

interface ReadOnlyTextProps<T = unknown> {
    icon?: IconName
    isSelected?: boolean
    displayValue: T
    readOnlyEmptyText?: string
    size: DropdownSize
    dataTestId?: string
}

const StyledReadOnlyTextContainer = styled.div<{ containerHeight?: number; textScale: TextScale; size: DropdownSize }>`
    box-sizing: border-box;
    display: flex;
    ${({ size }) => sizeMap[size ?? 'm'].readOnlyStyle};
    align-items: ${({ theme, textScale, containerHeight }) => getAlignItems(theme, textScale, containerHeight)};
    gap: ${({ theme }) => theme.spacing[100]};
    border-radius: 4px;
    border-left: 1px solid ${({ theme }) => theme.colors.border.primary};
    background: ${({ theme }) => theme.colors.surface.primary};
`

const ReadOnlyText: React.FC<ReadOnlyTextProps> = ({
    icon,
    isSelected,
    displayValue,
    readOnlyEmptyText,
    size,
    dataTestId,
}) => {
    const { t } = useTranslation()
    const labelRef = useRef<HTMLDivElement>(null)
    // This will be use to calculate alignItems
    const [offsetHeight, setOffsetHeight] = useState(labelRef.current?.offsetHeight)

    useEffect(() => {
        setOffsetHeight(labelRef.current?.offsetHeight)
    }, [labelRef.current?.offsetHeight])

    const textScale = isSelected ? 'bodyS700' : 'bodyS400'

    return (
        <StyledReadOnlyTextContainer
            containerHeight={offsetHeight}
            textScale={textScale}
            size={size}
            data-testid={dataTestId}
        >
            {icon && <Icon name={icon} size="20px" className="icon" color={isSelected ? 'primary' : 'disable'} />}
            <Text ref={labelRef} color={isSelected ? 'primary' : 'disable'} scale={textScale}>
                {displayValue
                    ? displayValue
                    : readOnlyEmptyText
                      ? readOnlyEmptyText
                      : t('placeholderText.dropdownMenu.noItem')}
            </Text>
        </StyledReadOnlyTextContainer>
    )
}

export interface DropdownProps<T = unknown> {
    className?: string
    isDisabled?: boolean
    size?: DropdownSize
    label?: string
    subtext?: string
    placeholder?: string
    icon?: IconName
    isReadOnly?: boolean
    readOnlyEmptyText?: string
    isRequired?: boolean
    isCommentable?: boolean
    isCommented?: boolean
    onLabelClick?: React.MouseEventHandler<HTMLDivElement>
    tooltipContext?: { title?: string; label?: string }
    errorMessage?: string
    showErrorStyle?: boolean
    multiple?: boolean
    width?: number | string
    children?: React.ReactNode
    onChange?: (value: T) => void
    onClose?: (event?: object, reason?: 'backdropClick' | 'escapeKeyDown') => void
    value?: T
    defaultValue?: T
    /* This property is to hide chips if multiple is true */
    hasChips?: boolean
    open?: boolean
    /* This property will be use to render a custom selected value */
    renderValue?: (value: T) => React.ReactNode
    identifier?: string
    maxWidth?: number | string
    /* Chip props to pass */
    chipProps?: Omit<ChipProps, 'label'>
    dataTestId?: string
    labelSize?: LabelSize
    link?: LinkConfig
    tooltip?: string
    id?: string
    onScrollBottomCallback?: () => void
    isLoading?: boolean
}

export const Dropdown = forwardRef<HTMLDivElement, DropdownProps>(
    (
        {
            className,
            isDisabled = false,
            isReadOnly = false,
            readOnlyEmptyText,
            isRequired = false,
            isCommentable = false,
            isCommented = false,
            size = 'm',
            label = '',
            subtext,
            placeholder,
            icon,
            onLabelClick,
            tooltipContext,
            errorMessage,
            multiple,
            hasChips = true,
            children,
            value: valueProp,
            defaultValue,
            onChange,
            onClose,
            open,
            renderValue,
            identifier,
            chipProps,
            dataTestId,
            labelSize,
            link,
            tooltip,
            id,
            onScrollBottomCallback,
            isLoading,
        },
        ref,
    ) => {
        const { iconSize, menuSize, paperHeight } = sizeMap[size]
        const [value, setValue] = useState(defaultValue)
        const dropdownRef = useRef<HTMLDivElement>(null)
        const displayTextRef = useRef<HTMLDivElement>(null)
        const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)

        const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
            setMenuAnchorEl(event.currentTarget)
            event.stopPropagation()
        }
        const handleCloseMenu = (event?: object, reason?: 'backdropClick' | 'escapeKeyDown') => {
            setMenuAnchorEl(null)
            onClose?.(event, reason)
        }

        const handleItemClick = (child: React.ReactNode) => (event?: React.MouseEvent<HTMLLIElement>) => {
            if (React.isValidElement<MenuItemTextProps | MenuItemCheckboxProps>(child)) {
                let newValue
                if (multiple) {
                    newValue = Array.isArray(value) ? value.slice() : []
                    const itemIndex = newValue.indexOf(child.props.value)

                    if (itemIndex === -1) {
                        newValue.push(child.props.value)
                    } else {
                        newValue.splice(itemIndex, 1)
                    }
                } else {
                    newValue = child.props.value
                }
                if (event) {
                    child.props.onClick?.(event)
                }
                if (value !== newValue) {
                    setValue(newValue)
                    onChange?.(newValue)
                }
            }
            if (!multiple) {
                handleCloseMenu()
            }
        }

        const isMenuOpen = Boolean(menuAnchorEl || open)
        const childrenArray = React.Children.toArray(children)

        // Added valueProp for other component to be able to control this
        useEffect(() => {
            setValue(valueProp)
        }, [valueProp])

        // Calculate the value to display and children components
        const [displayValue, displayChips, childrenComponents] = useMemo(() => {
            let displayValue = undefined
            const displayMultipleValue: string[] = []
            const childrenComponents: React.ReactNode[] = []
            const displayChips: React.ReactNode[] = []
            childrenArray.map(child => {
                if (React.isValidElement<MenuItemTextProps | MenuItemCheckboxProps>(child)) {
                    // Build children component with the props
                    childrenComponents.push(
                        React.cloneElement(child, {
                            size: menuSize,
                            onClick: handleItemClick(child),
                            selected:
                                multiple && Array.isArray(value)
                                    ? value.includes(child.props.value)
                                    : child.props.value !== undefined && value === child.props.value,
                            dataTestId: `${dataTestId}-option`,
                        }),
                    )
                    if (multiple && Array.isArray(value)) {
                        if (value.includes(child.props.value)) {
                            displayMultipleValue.push(child.props.text)
                            displayChips.push(
                                <StyledChip
                                    label={child.props.text}
                                    onDelete={e => {
                                        handleItemClick(child)()
                                        e.preventDefault()
                                    }}
                                    isDisabled={isDisabled}
                                    {...chipProps}
                                />,
                            )
                        }
                    } else {
                        if (child.props.value !== undefined && value === child.props.value) {
                            displayValue = child.props.text
                            return
                        }
                    }
                } else {
                    childrenComponents.push(child)
                }
            })
            // Convert the value array to comma separated display
            if (multiple) {
                displayValue = displayMultipleValue.join(', ')
            }
            return [displayValue, displayChips, childrenComponents]
        }, [childrenArray, multiple])

        const isSelected = useMemo(() => {
            if (multiple) {
                return Array.isArray(value) && value.length > 0
            } else {
                return typeof value !== 'undefined'
            }
        }, [value, multiple])

        const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
            const target = event.currentTarget
            const bottom = target.scrollHeight - target.scrollTop === target.clientHeight
            if (bottom && onScrollBottomCallback) {
                onScrollBottomCallback()
            }
        }

        const displayValueOverflowTooltip = (() => {
            if (!displayTextRef.current) {
                return
            }

            return displayTextRef.current.scrollWidth > displayTextRef.current.clientWidth
                ? displayValue || placeholder
                : undefined
        })()

        return (
            <div ref={ref} className={className}>
                {(label || subtext) && (
                    <CommentContainer identifier={identifier}>
                        <Label
                            title={label}
                            subtext={subtext}
                            isRequired={isRequired}
                            size={labelSize ? labelSize : size === 'xs' ? 's' : size}
                            isCommentable={isCommentable}
                            isCommented={isCommented}
                            onClick={onLabelClick}
                            link={link}
                            tooltip={tooltip}
                        />
                    </CommentContainer>
                )}
                {!isReadOnly ? (
                    <div className="container" data-testid={dataTestId} id={id}>
                        <div className="chip-dropdown-container">
                            <div
                                ref={dropdownRef}
                                className={`dropdown ${isDisabled ? 'disabled' : undefined} ${
                                    isMenuOpen ? 'menu-focus' : undefined
                                }`}
                                tabIndex={0}
                                onClick={handleOpenMenu}
                            >
                                <div className="input-container">
                                    {icon && (
                                        <Icon
                                            name={icon}
                                            size="20px"
                                            className="icon"
                                            color={isDisabled ? 'disable' : isSelected ? 'primary' : 'placeholder'}
                                        />
                                    )}
                                    {renderValue && isSelected ? (
                                        renderValue(value)
                                    ) : (
                                        <Tooltip label={displayValueOverflowTooltip}>
                                            <Text
                                                ref={displayTextRef}
                                                scale="bodyS400"
                                                className="text"
                                                color={isDisabled ? 'disable' : isSelected ? 'primary' : 'placeholder'}
                                            >
                                                {displayValue || placeholder}
                                            </Text>
                                        </Tooltip>
                                    )}
                                </div>
                                {!isDisabled ? (
                                    <Icon
                                        name="caretDown"
                                        size={iconSize}
                                        color="neutralAction"
                                        className="caret-icon"
                                    />
                                ) : tooltipContext?.label || tooltipContext?.title ? (
                                    <Tooltip {...tooltipContext} onClick={e => e.stopPropagation()}>
                                        <IconButton
                                            size="xs"
                                            color="neutral"
                                            form="icon"
                                            icon="question"
                                            className="tooltip"
                                        />
                                    </Tooltip>
                                ) : (
                                    <></>
                                )}
                            </div>
                            {multiple && hasChips && !isReadOnly && displayChips.length > 0 && (
                                <div className="chip-container">{displayChips}</div>
                            )}
                        </div>
                        {!isDisabled && errorMessage && (
                            <Text scale="caption400" color="error" className="error">
                                {errorMessage}
                            </Text>
                        )}
                    </div>
                ) : (
                    <ReadOnlyText
                        icon={icon}
                        isSelected={isSelected}
                        displayValue={renderValue ? renderValue(value) : displayValue}
                        readOnlyEmptyText={readOnlyEmptyText}
                        size={size}
                        dataTestId={`${dataTestId}-readOnly`}
                    />
                )}

                <StyledMenu
                    size={menuSize}
                    open={isMenuOpen}
                    anchorEl={dropdownRef.current}
                    onClose={handleCloseMenu}
                    width={dropdownRef.current?.offsetWidth}
                >
                    <StyledPaper
                        style={{
                            maxHeight: paperHeight,
                        }}
                        onScroll={onScrollBottomCallback && handleScroll}
                        className="test-papper"
                    >
                        {childrenComponents}
                    </StyledPaper>
                    {isLoading && <Loader className="loader" size="l" />}
                </StyledMenu>
            </div>
        )
    },
)

Dropdown.displayName = 'Dropdown'

const StyledMenu = styled(Menu)`
    .loader {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        background-color: rgba(242, 242, 242, 0.3);
        z-index: 200;
    }
`

const StyledPaper = styled(Paper)`
    overflow-y: auto;
    overflow-x: hidden;

    ::-webkit-scrollbar {
        -webkit-appearance: none;
        width: 5px;
    }
    ::-webkit-scrollbar-thumb {
        border-radius: 4px;
        background-color: ${({ theme }) => theme.colors.surface.neutralActionHover};
    }
`

export default styled(Dropdown)`
    display: flex;
    width: ${({ width }) => (width ? (typeof width === 'number' ? `${width}px` : width) : '100%')};
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[200]};
    .container {
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing[100]};
        .chip-dropdown-container {
            display: flex;
            flex-direction: column;
            gap: ${({ theme }) => theme.spacing[200]};
            width: 100%;
        }
        .dropdown {
            box-sizing: border-box;
            display: flex;
            height: 40px;
            ${({ size }) => sizeMap[size ?? 'm'].style};
            max-width: ${({ maxWidth }) =>
                maxWidth ? (typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth) : '600px'};
            align-items: center;
            gap: ${({ theme }) => theme.spacing[200]};
            border-radius: 4px;
            border: 1px solid
                ${({ theme, isDisabled, errorMessage, showErrorStyle }) =>
                    !isDisabled && (errorMessage || showErrorStyle)
                        ? theme.colors.border.error
                        : theme.colors.border.primary};
            background: ${({ theme }) => theme.colors.surface.primary};
            cursor: pointer;
            &.disabled {
                background: ${({ theme }) => theme.colors.surface.disablePastel};
                pointer-events: none;
                .tooltip {
                    pointer-events: all;
                }
            }
            .input-container {
                display: flex;
                align-items: center;
                gap: ${({ theme }) => theme.spacing[100]};
                flex: 1 0 0;
                min-width: 0;
                .text {
                    text-overflow: ellipsis;
                    overflow: hidden;
                    flex: 1 0 0;
                    white-space: nowrap;
                }
            }
            .caret-icon,
            .icon {
                flex-shrink: 0;
            }
            &.menu-focus,
            &:focus-visible {
                border-color: ${({ theme, errorMessage }) =>
                    errorMessage ? theme.colors.border.error : theme.colors.border.neutralActionActive};
                outline: none;
                .caret-icon {
                    fill: ${({ theme }) => theme.colors.icon.neutralActionActive};
                }
                .icon {
                    fill: ${({ theme }) => theme.colors.icon.primary};
                }
            }
        }
    }
    .chip-container {
        display: flex;
        gap: ${({ theme }) => theme.spacing[200]};
        flex-wrap: wrap;
    }
`
